import { Column, TableOptions, useFilters, usePagination, useSortBy, useTable } from 'react-table'
import React, { useEffect, useState } from 'react'

import { SliderCheckbox } from './SliderCheckbox'
import Styles from './Table.module.css'
import { downloadCSV } from '../Helpers/files'

//Styles

const DEFAULT_PAGE_SIZE = 25

export const Table = <T extends {}>({
    data,
    columns,
    csvFileName,
    canDownloadCsv = true,
    updateMyData,
    ...rest
}: TableProps<T>) => {
    const {
        getTableProps,
        getTableBodyProps,
        prepareRow,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        headerGroups,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        state: { pageIndex, pageSize },
        columns: allColumns,
    } = useTable<T>(
        {
            data,
            columns,
            updateMyData,
            ...rest,
        },
        useFilters,
        useSortBy,
        usePagination,
    )

    useEffect(() => setPageSize(pageSize || DEFAULT_PAGE_SIZE), [data, setPageSize])

    if (!data.length) return null

    const isFiltrable = !!allColumns.filter((col) => !!col.Filter).length

    const csvHeaders = canDownloadCsv && getHeaders(columns)
    const csvData = rows.map(({ values }) =>
        Object.values(values)
            .map((value) => (typeof value === 'string' || typeof value === 'number' ? value : ''))
            .join(';'),
    )

    const dataForCSV = [csvHeaders].concat(csvData)
    const cvsFileName = csvFileName ? `${csvFileName.replaceAll(' ', '_')}.csv` : 'table_data.csv'

    return (
        <>
            {
                // Show filter if some column has filter property
                isFiltrable && (
                    <table className={Styles.filter} {...getTableProps()}>
                        <thead style={{ display: 'flex' }}>
                            {headerGroups.map((headerGroup) => (
                                <tr
                                    {...headerGroup.getHeaderGroupProps()}
                                    key={headerGroup.getHeaderGroupProps().key}
                                    className="row-disposition"
                                >
                                    {headerGroup.headers.map(
                                        (column) =>
                                            column.Filter && (
                                                <th
                                                    {...column.getHeaderProps(
                                                        column.getSortByToggleProps(),
                                                    )}
                                                    key={column.getHeaderProps().key}
                                                    style={{ cursor: 'default', margin: 0 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {column.render('Filter', {
                                                        data,
                                                        helper: true,
                                                    })}
                                                </th>
                                            ),
                                    )}
                                </tr>
                            ))}
                        </thead>
                    </table>
                )
            }

            {canDownloadCsv && (
                <button
                    className={`outlined ${Styles['downloadCSV']}`}
                    onClick={() => downloadCSV(dataForCSV, cvsFileName)}
                >
                    Descargar CSV
                </button>
            )}

            <table {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                        >
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    key={column.getHeaderProps().key}
                                >
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted && (column.isSortedDesc ? ' ↑ ' : ' ↓ ')}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                    {(data.length > (pageSize ?? DEFAULT_PAGE_SIZE) ? page : rows).map((row) => {
                        prepareRow(row)
                        const { key, ...restRowProps } = row.getRowProps()
                        return (
                            <tr key={key} {...restRowProps}>
                                {row.cells.map((cell) => {
                                    const { key, ...restCellProps } = cell.getCellProps()
                                    return (
                                        <td key={key} {...restCellProps}>
                                            {cell.render('Cell')}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>

            {data.length > (pageSize || DEFAULT_PAGE_SIZE) && (
                <div className={Styles['pagination']}>
                    <button
                        className="outlined"
                        onClick={() => gotoPage(0)}
                        disabled={!canPreviousPage}
                    >
                        {'<<'}
                    </button>
                    <button
                        className="outlined"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                    >
                        {'<'}
                    </button>
                    <span>
                        {pageIndex + 1} / {pageOptions.length}
                    </span>
                    <button className="outlined" onClick={() => nextPage()} disabled={!canNextPage}>
                        {'>'}
                    </button>
                    <button
                        className="outlined"
                        onClick={() => gotoPage(pageCount - 1)}
                        disabled={!canNextPage}
                    >
                        {'>>'}
                    </button>
                </div>
            )}
        </>
    )
}

export const Filter = <T extends {}>({ column, data, helper }: FilterProps<T> | any) => {
    const { Header, filter: defaultFilter, filterValue, setFilter, id } = column
    const [help, setHelp] = useState(helper)

    useEffect(() => {
        setFilter(defaultFilter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilter])

    const handleList = () => {
        // let arrayOfValues: T[keyof T][] = []
        //@ts-expect-error type error
        const arrayOfValues = data.map((item) => item[id])
        const option = [...new Set(arrayOfValues)].map((item) => {
            if (typeof item === 'object') return
            if (typeof item === 'boolean')
                return <option key={`${item}`}>{item && 'Activo'}</option>
            //@ts-expect-error type error
            return <option key={`${item}`}>{item}</option>
        })
        return option
    }

    const handleValue = (value: string) => {
        switch (value) {
            case 'Activo':
                return true
            case 'Inactivo':
                return false
            default:
                return value
        }
    }

    return (
        <>
            <div className="flex-search">
                <p>{Header}</p>
                <SliderCheckbox
                    title={`${help ? 'Desactivar' : 'Activar'} sugerencias de búsqueda`}
                    id={`checkbox-${String(id)}`}
                    checked={help}
                    onChange={() => setHelp(!help)}
                />
            </div>
            <input
                type="search"
                value={filterValue}
                onChange={({ target: { value } }) => {
                    setFilter(handleValue(value))
                }}
                onClick={(e) => e.stopPropagation()}
                list={`list-${String(id)}`}
            />
            {
                //helper
                help && <datalist id={`list-${String(id)}`}>{handleList()}</datalist>
            }
        </>
    )
}

function updateOneRow<T>(data: T[], rowIndex: number, columnId: string, value: unknown) {
    return data.map((row, index) => {
        if (index === rowIndex) {
            return {
                ...data[rowIndex],
                [columnId]: value,
            }
        }
        return row
    })
}

function update<T>(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    validator: Function | undefined,
    rowIndex: number,
    columnId: string,
    value: unknown,
) {
    setter((data) =>
        validator
            ? validator(updateOneRow(data, rowIndex, columnId, value))
            : updateOneRow(data, rowIndex, columnId, value),
    )
}

export const EditableCell = ({
    value,
    row,
    column,
    updateMyData,
    setter,
    validator,
}: EditableProps) => {
    const [newValue, setNewValue] = useState(value)
    const { index } = row
    const { id } = column

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setNewValue(e.target.value)

    const onBlur = () =>
        updateMyData
            ? updateMyData(index, id, newValue)
            : setter && update(setter, validator, index, id, newValue)

    useEffect(() => setNewValue(value), [value])

    return (
        <input
            style={{ border: '1px solid grey', padding: '7px' }}
            value={newValue}
            onChange={onChange}
            onBlur={onBlur}
        />
    )
}

const getHeaders = <T extends Record<string, unknown>>(
    columns: Column<T>[],
    separator = ': ',
    parent?: Column['Header'],
): string =>
    columns
        .map((column) =>
            !('columns' in column)
                ? `${parent ? parent + separator : ''}${column.Header}`
                : getHeaders(column.columns, separator, `${column.Header}`),
        )
        .join(';')

// Interface
interface TableProps<T extends Record<string, unknown>>
    extends React.DetailedHTMLProps<React.TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>,
        TableOptions<T> {
    data: T[]
    columns: Column<T>[]
    withPagination?: boolean
    csvFileName?: string
    canDownloadCsv?: boolean
    // eslint-disable-next-line no-unused-vars
    updateMyData?: EditableProps['updateMyData']
    setter?: EditableProps['setter']
    validator?: EditableProps['validator']
}

interface FilterProps<T extends Record<string, unknown>> {
    data: T[]
    helper: boolean
    column: {
        filter: string
        Header: string
        id: keyof T
        filterValue: string
        setFilter: React.Dispatch<React.SetStateAction<string | boolean>>
    }
}

interface EditableProps {
    value: string
    row: {
        index: number
    }
    column: {
        id: string
    }
    // eslint-disable-next-line no-unused-vars
    updateMyData?: (index: number, id: string, value: any) => void
    setter?: React.Dispatch<React.SetStateAction<any>>
    // eslint-disable-next-line no-unused-vars
    validator?: (items: any) => void
}
